import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { ConversationHeader, ConversationRow } from '@/features/support/components/conversation-row.tsx'
import { ConversationView } from '@/features/support/components/conversation-view.tsx'
import { useConversation, useConversations, useSupportStats } from '@/features/support/use-support.ts'
import { FullScreenLayer } from '@/features/support-chat/components/chat-screen.tsx'
import { ApiError } from '@/shared/api/client.ts'
import { useWideScreen } from '@/shared/hooks/use-media-query.ts'
import { plural } from '@/shared/lib/format.ts'
import { Card } from '@/shared/ui/card.tsx'
import { Chip } from '@/shared/ui/chip.tsx'
import { Alert, EmptyState, ErrorState, Skeleton } from '@/shared/ui/states.tsx'

const SELECTED_PARAM = 'c'

function BackToList({ onBack }: { onBack?: () => void }) {
  if (!onBack) return null
  return (
    <button
      type="button"
      onClick={onBack}
      className="flex min-h-[44px] items-center px-4 pt-[env(safe-area-inset-top)] text-left text-[15px] text-brand"
    >
      ‹ К обращениям
    </button>
  )
}

function Reader({ id, onBack }: { id: string; onBack?: () => void }) {
  const detail = useConversation(id)

  if (detail.isPending) {
    return (
      <div className="flex flex-col">
        <BackToList onBack={onBack} />
        <div className="flex flex-col gap-3 p-5">
          <Skeleton className="h-14" />
          <Skeleton className="h-24" />
        </div>
      </div>
    )
  }
  if (!detail.data) {
    return (
      <div className="flex flex-col">
        <BackToList onBack={onBack} />
        <ErrorState
          message={detail.error instanceof ApiError ? detail.error.message : 'Не удалось открыть обращение.'}
          onRetry={() => void detail.refetch()}
        />
      </div>
    )
  }
  return (
    <div className="flex h-full min-h-0 flex-col bg-canvas">
      <ConversationHeader item={detail.data.conversation} onBack={onBack} />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
        {detail.data.messages.length === 0 ? (
          <EmptyState title="Пусто" description="В этом обращении нет реплик — только служебные события." />
        ) : (
          <ConversationView messages={detail.data.messages} />
        )}
      </div>
    </div>
  )
}

export function SupportPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const wide = useWideScreen()
  const selected = searchParams.get(SELECTED_PARAM)

  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [escalatedOnly, setEscalatedOnly] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const list = useConversations({ query, escalatedOnly })
  const stats = useSupportStats()
  const conversations = list.data?.conversations ?? []

  // The open conversation lives in the URL, so the phone's back button closes it.
  const select = (id: string) =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set(SELECTED_PARAM, id)
        return next
      },
      // Two panes switch in place; on a phone opening is a step the back button can undo.
      wide ? { replace: Boolean(selected) } : { state: { fromList: true } },
    )
  const closeReader = () =>
    (location.state as { fromList?: boolean } | null)?.fromList
      ? navigate(-1)
      : setSearchParams((params) => {
          const next = new URLSearchParams(params)
          next.delete(SELECTED_PARAM)
          return next
        }, { replace: true })

  if (list.data && !list.data.available) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeading />
        <Alert>
          История обращений не подключена: в настройках сервера не задан адрес базы
          (<code>SUPPORT_DATABASE_URL</code>). Раздел заработает, как только он появится.
        </Alert>
      </div>
    )
  }

  const listCard = (
    <Card className="-mx-4 flex min-h-0 flex-col overflow-hidden rounded-none border-x-0 lg:mx-0 lg:h-[calc(100vh-230px)] lg:min-h-[480px] lg:rounded-card lg:border-x">
      <label className="flex items-center gap-2.5 border-b border-line-soft px-4 py-3 md:px-5">
        <Search className="size-4 shrink-0 text-ink-faint" aria-hidden />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Поиск по теме и тексту диалога…"
          enterKeyHint="search"
          className="w-full bg-transparent text-[16px] outline-none placeholder:text-ink-faint md:text-[14.5px]"
        />
      </label>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        ) : list.error ? (
          <ErrorState
            message={list.error instanceof ApiError ? list.error.message : 'Не удалось загрузить обращения.'}
            onRetry={() => void list.refetch()}
          />
        ) : conversations.length === 0 ? (
          <EmptyState
            title={query ? 'Ничего не нашли' : 'Обращений пока нет'}
            description={
              query
                ? 'Попробуйте другую формулировку — поиск идёт по теме и по тексту сообщений.'
                : 'Как только салон напишет боту, обращение появится здесь.'
            }
          />
        ) : (
          conversations.map((item) => (
            <ConversationRow key={item.id} item={item} active={item.id === selected} onSelect={() => select(item.id)} />
          ))
        )}
      </div>

      {list.data && list.data.total > conversations.length && (
        <p className="border-t border-line-soft px-5 py-3 text-[12.5px] text-ink-muted">
          Показано {conversations.length} из {list.data.total}{' '}
          {plural(list.data.total, ['обращения', 'обращений', 'обращений'])}
        </p>
      )}
    </Card>
  )

  return (
    <div className="flex flex-col gap-4 lg:gap-5">
      <PageHeading />

      {stats.data && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
          <span className="text-[13px] text-ink-muted">
            Всего <span className="tabular font-medium text-ink">{stats.data.total}</span>
          </span>
          <span className="text-[13px] text-ink-muted">
            За неделю <span className="tabular font-medium text-ink">{stats.data.last7Days}</span>
          </span>
          <Chip
            label="Передано команде"
            count={stats.data.escalated}
            active={escalatedOnly}
            tone="tint"
            onClick={() => setEscalatedOnly((value) => !value)}
          />
        </div>
      )}

      {wide ? (
        <div className="grid grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-5">
          {listCard}
          <Card className="h-[calc(100vh-230px)] min-h-[480px] overflow-hidden">
            {selected ? (
              <Reader key={selected} id={selected} />
            ) : (
              <EmptyState title="Выберите обращение" description="Слева список — откройте любое, чтобы прочитать диалог целиком." />
            )}
          </Card>
        </div>
      ) : (
        <>
          {listCard}
          {selected && (
            <FullScreenLayer>
              <Reader key={selected} id={selected} onBack={closeReader} />
            </FullScreenLayer>
          )}
        </>
      )}
    </div>
  )
}

function PageHeading() {
  return (
    <header className="flex flex-col gap-1">
      <h1 className="font-display text-[28px] leading-tight font-semibold tracking-[-0.02em] lg:text-[22px] lg:font-medium lg:tracking-[-0.015em]">
        Поддержка
      </h1>
      <p className="text-[14px] text-ink-muted">
        Обращения салонов к боту: с чем приходят, что ответил бот и где он передал вопрос команде.
      </p>
    </header>
  )
}
